import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle, Shield, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Hero: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const highlightedWords = ["bloqueados", "suspensos...", "já retidos"];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const wordInterval = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % highlightedWords.length);
    }, 4000); // Alterna a cada 4 segundos
    
    return () => clearInterval(wordInterval);
  }, []);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { type: 'spring', damping: 12 } }
  };

  const cardVariants = {
    hover: { scale: 1.05, y: -5 }
  };

  return (
    <section className="relative pt-32 sm:pt-36 md:pt-44 lg:pt-52 pb-16 md:pb-24 lg:pb-32 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white overflow-hidden">
      {/* Elementos de fundo decorativos */}
      <div 
        className="absolute inset-0 bg-[url('/direito_bancario.jpg')] bg-cover bg-center opacity-10"
        style={{ transform: `translateY(${scrollY * 0.2}px)` }}
      ></div>
      
      {/* Partículas animadas */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div 
            key={i}
            className="absolute rounded-full bg-accent/20"
            initial={{ 
              x: Math.random() * 100, 
              y: -20, 
              scale: Math.random() * 0.8 + 0.2,
              opacity: Math.random() * 0.5 + 0.3
            }}
            animate={{ 
              y: window.innerHeight + 50,
              opacity: 0,
              transition: { 
                repeat: Infinity, 
                duration: Math.random() * 20 + 10,
                delay: Math.random() * 10
              }
            }}
            style={{
              width: `${Math.random() * 40 + 10}px`,
              height: `${Math.random() * 40 + 10}px`,
              left: `${Math.random() * 100}%`
            }}
          />
        ))}
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        <motion.div 
          className="max-w-5xl mx-auto"
          initial="hidden"
          animate="show"
          variants={container}
        >
          <div className="text-center md:text-left lg:flex lg:items-center lg:justify-between lg:gap-12">
            <div className="lg:max-w-3xl">
              <motion.h1 
                className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl xl:text-7xl mb-6 sm:mb-8 leading-tight text-shadow-sm"
                variants={item}
              >
                Recupere os valores que foram{' '}
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={currentWordIndex}
                    className="text-accent bg-gradient-to-r from-accent to-blue-400 text-transparent bg-clip-text"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.8 }}
                  >
                    {highlightedWords[currentWordIndex]}
                  </motion.span>
                </AnimatePresence>{' '}
                da sua conta por decisão judicial!
              </motion.h1>
              
              <motion.p 
                className="text-xl sm:text-2xl md:text-3xl mb-8 sm:mb-10 text-white/90 font-light max-w-3xl mx-auto lg:mx-0"
                variants={item}
              >
                Assessoria jurídica completa para liberação de penhora.
              </motion.p>
              
              <motion.div 
                className="mb-10 sm:mb-12"
                variants={item}
              >
                <motion.button 
  onClick={() => {
    const message = "Encontrei seu contato pelo site e gostaria de solicitar algumas informações!";
    window.open(`https://wa.me/5581993507689?text=${encodeURIComponent(message)}`, '_blank');
  }}
  className="bg-gradient-to-r from-accent to-blue-500 hover:from-accent/90 hover:to-blue-600 text-white font-semibold py-4 sm:py-5 px-8 sm:px-12 rounded-full shadow-xl transition-all duration-300 inline-flex items-center group text-lg"
  whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(234, 88, 12, 0.4)' }}
  whileTap={{ scale: 0.98 }}
>
  Fale com um especialista
  <motion.div
    animate={{ x: [0, 5, 0] }}
    transition={{ repeat: Infinity, duration: 1, repeatDelay: 1 }}
  >
    <ArrowRight size={22} className="ml-3" />
  </motion.div>
</motion.button>
              </motion.div>
            </div>

            {/* Estatísticas - Apenas visível em telas grandes */}
            <motion.div 
              className="hidden lg:block bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 shadow-2xl w-full max-w-sm"
              variants={item}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <h3 className="text-xl font-semibold text-white/90 mb-6 text-center">Nós já ajudamos</h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Clientes atendidos</span>
                  <span className="text-accent font-bold text-xl">+ 500</span>
                </div>
                <div className="h-px bg-white/10 w-full"></div>
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Valor recuperado</span>
                  <span className="text-accent font-bold text-xl">+ R$ 5 mi</span>
                </div>
                <div className="h-px bg-white/10 w-full"></div>
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Índice de sucesso</span>
                  <span className="text-accent font-bold text-xl">98%</span>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 mt-16 sm:mt-20"
            variants={item}
          >
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 shadow-xl flex flex-col items-center justify-center"
              variants={cardVariants}
              whileHover="hover"
            >
              <motion.div 
                className="bg-accent/20 p-4 rounded-full mb-5"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <Clock size={32} className="text-accent" />
              </motion.div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-accent mb-3">Rapidez</h3>
              <p className="text-white/80 text-center text-base md:text-lg">Atuação rápida para desbloquear seus valores o quanto antes</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 shadow-xl flex flex-col items-center justify-center"
              variants={cardVariants}
              whileHover="hover"
            >
              <motion.div 
                className="bg-accent/20 p-4 rounded-full mb-5"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <Shield size={32} className="text-accent" />
              </motion.div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-accent mb-3">Experiência</h3>
              <p className="text-white/80 text-center text-base md:text-lg">Especialistas em direito bancário e desbloqueio de contas</p>
            </motion.div>
            
            <motion.div 
              className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 shadow-xl flex flex-col items-center justify-center sm:col-span-2 md:col-span-1"
              variants={cardVariants}
              whileHover="hover"
            >
              <motion.div 
                className="bg-accent/20 p-4 rounded-full mb-5"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircle size={32} className="text-accent" />
              </motion.div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-accent mb-3">Resultados</h3>
              <p className="text-white/80 text-center text-base md:text-lg">Altos índices de sucesso na recuperação de valores</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Adicionamos uma sombra para melhorar a legibilidade do texto */}

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/20 to-transparent"></div>
    </section>
  );
};

export default Hero;